import React, { useMemo, useState } from 'react';

import Pen from '@ast/magma/components/icon/icons/Pen';

import { Button } from '@ast/magma/components/button';

import { SelectableGroupFieldFragment } from '@app/common/configurable-wizards/queries/queryTyping/all-fields';
import {
  FieldComponent,
  FieldLocator,
} from '@app/common/configurable-wizards/types';

import { DeleteModal } from '@app/widgets/contacts-and-settings/components/DeleteModal';
import { SettingsSection } from '@app/widgets/contacts-and-settings/components/SettingsSection';
import { useSettingFieldsContext } from '@app/widgets/contacts-and-settings/contexts/SettingFieldsContext';
import { useContactFieldMeta } from '@app/widgets/contacts-and-settings/hooks/useContactFieldMeta';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

import {
  AddressType,
  RemoveUserContact,
} from '../../components/DeleteModal/DeleteModal';
import { SettingFieldsProvider } from '../../contexts/AddressFormContext';
import { AddressFieldGroups } from '../../types';
import { getSelectedAddressField } from '../../utils/address';
import {
  canRemoveAddress,
  canEditAddress,
} from '../../utils/addressPermissions';
import { formatAddress } from '../../utils/labelFormatters';

import { AddressEditForm } from './AddressEditForm';
import styles from './AddressGroupDetails.pcss';

export const AddressGroupDetails: FieldComponent<SelectableGroupFieldFragment> = ({ field: parentField }) => {
  const { t } = useUserSettingsTranslation();
  const { permissions } = useSettingFieldsContext();
  const field = getSelectedAddressField(parentField);
  const { fieldMeta } = useContactFieldMeta(field);

  const contact = useMemo(
    () => ({
      contactId: fieldMeta?.UniqueId?.sValue,
      contactType: AddressType.Address,
    } as RemoveUserContact),
    [fieldMeta],
  );
  const addressLabel = useMemo(() => formatAddress(fieldMeta), [fieldMeta]);
  const canRemove = useMemo(() => canRemoveAddress(field), [field]);
  const canEdit = useMemo(
    () => canEditAddress(fieldMeta, permissions),
    [fieldMeta, permissions],
  );
  const [isOpen, setIsOpen] = useState(false);

  return (
    <SettingsSection
      sectionHeader={(
        <>
          <div className={styles.addressDetails}>
            <span data-stable-name="AddressLabel">
              {addressLabel}
            </span>
          </div>
          {canEdit && (
            <>
              <Button
                look="icon"
                data-stable-name="EditActionButton"
                icon={<Pen />}
                aria-label={t(
                  'address-field-list-item-details.action-button.aria-label|Tooltip text for edit address action button',
                  'Edit address',
                )}
                onClick={() => setIsOpen(true)}
              />
              {isOpen && (
                <SettingFieldsProvider>
                  <AddressEditForm
                    field={parentField}
                    onClose={() => setIsOpen(false)}
                    successMessage={t(
                      'address-field-list-item-details.update-dialog-success.text|Text for a title on Edit address dialog',
                      'Your address has been successfully changed',
                    )}
                    dialogOptions={{
                      title: t(
                        'address-field-list-item-details.edit-dialog.title-label|Text for a title on Edit address dialog',
                        'Edit address',
                      ),
                      showClose: {
                        // eslint-disable-next-line i18next/no-literal-string
                        'aria-label': t(
                          'address-field-list-item-details.edit-dialog.close-button.aria-label|Text for a close button on Edit address dialog',
                          'Close edit address dialog',
                        ),
                      },
                    }}
                  />
                </SettingFieldsProvider>

              )}
            </>
          )}
          {canRemove && canEdit && (
            <DeleteModal
              contact={contact}
              successMessage={t(
                'address-field-list-item-details.delete-dialog.success-message|Message text for succesful delete address',
                'Your address has been successfully removed',
              )}
              dialogOptions={{
                title: t(
                  'mailing-address-field-list-item-details.delete-dialog.title|Delete address dialog title',
                  'Delete mailing address',
                ),
              }}
            >
              {
                t(
                  // eslint-disable-next-line i18next/no-literal-string
                  'address-field-list-item-details.delete-dialog.message|Delete address dialog message',
                  'Are you sure you want to delete your <strong>Mailing</strong> address from your contact information?',
                )
              }
            </DeleteModal>
          )}
        </>
      )}
      sectionNote={t(
        'address-field-list-item-details.address-usage-explanation.text|Address usage description',
        'Your address can be used to deliver important information, help you sign in, and more',
      )}
    />
  );
};

export const DetailAddressFieldLocator: FieldLocator = ({ field }) => (
  field.id in AddressFieldGroups
    ? AddressGroupDetails
    : undefined
);
